import { Box, Checkbox, Tooltip } from "@mui/material";
import type { ColDef, ICellRendererParams } from "ag-grid-community";

import { InfoOutlined, ReportProblemOutlined } from "@mui/icons-material";
import {
	isAngleSpecItem,
	isGaugeSpecItem,
} from "@/shared/utils/sectionSpecUnits";
import {
	formatSpecAngle,
	SectionSpecAngleEditor,
} from "../components/section-spec/SectionSpecAngleEditor";
import {
	formatSectionSpecDimensionValue,
	SPEC_BAND_FIELD,
	type SpecBandField,
	type SpecItemDraftRow,
} from "../models/SectionSpecForm";

interface UseSectionSpecTableDataProps {
	onDegreesChange?: (profileItemId: number, isDegrees: boolean) => void;
	angleInputs?: boolean;
	showType?: boolean;
	showDegrees?: boolean;
	showBands?: boolean;
	getError?: (
		profileItemId: number,
		field: SpecBandField,
	) => string | undefined;
	getToggleLock?: (row: SpecItemDraftRow) => string | undefined;
	onEdit: (profileItemId: number, field: SpecBandField, value: string) => void;
	onToggle: (profileItemId: number, applies: boolean) => void;
	readOnly: boolean;
}

const UseDegreesHeader = () => (
	<Box
		component="span"
		sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
	>
		<span>Use Degrees</span>
		<Tooltip arrow title="Enter values in degrees and minutes">
			<Box
				component="span"
				tabIndex={0}
				aria-label="Use Degrees information"
				sx={{ display: "inline-flex", color: "primary.main", cursor: "help" }}
			>
				<InfoOutlined sx={{ fontSize: 15 }} />
			</Box>
		</Tooltip>
	</Box>
);

// Gauge specifications have numeric bands even though inspections use checkboxes.
const hasNumericBands = (row: SpecItemDraftRow) =>
	row.ValueTypeCode !== "A" ||
	(row.CategoryName === "Go / No Go" && /^(?:[5-9]|1[0-4])$/.test(row.Code));

const getAppliesAriaLabel = (dimension?: SpecItemDraftRow) =>
	dimension
		? `${dimension.Name} (${dimension.Code}) applies`
		: "Dimension applies";

const bandColumn = (
	field: SpecBandField,
	headerName: string,
	readOnly: boolean,
	onEdit: UseSectionSpecTableDataProps["onEdit"],
	getError?: UseSectionSpecTableDataProps["getError"],
	angleInputs = false,
): ColDef<SpecItemDraftRow, string> => ({
	field,
	headerName,
	width: angleInputs ? 150 : 100,
	type: "centerAligned",
	sortable: false,
	resizable: true,
	cellClassRules: {
		"section-spec-cell-error": (params) =>
			Boolean(params.data && getError?.(params.data.ProfileItemId, field)),
	},
	tooltipValueGetter: (params) =>
		params.data ? getError?.(params.data.ProfileItemId, field) : undefined,
	editable: (params) =>
		!readOnly && params.data?.Applies === true && hasNumericBands(params.data),
	// Non-gauge checkbox bands remain not applicable without changing stored values.
	cellRendererSelector: (params) =>
		params.data && !hasNumericBands(params.data)
			? {
					component: () => (
						<Box
							component="span"
							data-spec-band-not-applicable
							aria-label="Not applicable"
							sx={{
								alignItems: "center",
								color: "text.disabled",
								display: "flex",
								height: "100%",
								justifyContent: "center",
								width: "100%",
							}}
						>
							—
						</Box>
					),
				}
			: undefined,
	// Text inputs preserve invalid lexemes so users can correct them.
	cellEditor: "agTextCellEditor",
	cellEditorSelector: (params) =>
		angleInputs && params.data && isAngleSpecItem(params.data)
			? { component: SectionSpecAngleEditor }
			: undefined,
	suppressKeyboardEvent: (params) => {
		const input = params.event.target;
		return (
			angleInputs &&
			params.editing &&
			params.event.key === "Tab" &&
			input instanceof HTMLInputElement &&
			input.dataset.anglePart === (params.event.shiftKey ? "Min" : "Deg")
		);
	},
	valueParser: (params) => String(params.newValue ?? ""),
	valueSetter: (params) => {
		const nextValue = formatSectionSpecDimensionValue(
			params.newValue ?? "",
			params.data,
		);
		if (Object.is(nextValue, params.oldValue)) return false;
		// Commit to the form synchronously before Save reads it; never mutate its rows.
		onEdit(params.data.ProfileItemId, field, nextValue);
		return false;
	},
	valueFormatter: (params) =>
		angleInputs && params.data && isAngleSpecItem(params.data)
			? formatSpecAngle(params.value ?? "")
			: formatSectionSpecDimensionValue(
					params.value ?? "",
					params.data ?? { Code: "" },
				),
});

export const useSectionSpecTableData = ({
	onDegreesChange,
	angleInputs = false,
	showType = false,
	showDegrees = true,
	showBands = true,
	getError,
	getToggleLock,
	onEdit,
	onToggle,
	readOnly,
}: UseSectionSpecTableDataProps) => {
	const columnDefs: ColDef<SpecItemDraftRow>[] = [
		{
			headerName: "Applies",
			field: "Applies",
			width: 100,
			sortable: false,
			resizable: true,
			type: "centerAligned",
			cellRenderer: (params: ICellRendererParams<SpecItemDraftRow>) => {
				const lock = params.data ? getToggleLock?.(params.data) : undefined;
				return (
					<Box
						sx={{
							alignItems: "center",
							display: "flex",
							height: "100%",
							justifyContent: "center",
						}}
					>
						<Tooltip title={lock ?? ""} describeChild>
							<Box sx={{ display: "inline-flex", position: "relative" }}>
								{lock !== undefined && (
									<Box
										sx={{
											display: "flex",
											position: "absolute",
											right: "100%",
											top: "50%",
											transform: "translateY(-50%)",
										}}
									>
										<Box
											component="span"
											sx={{ display: "inline-flex", color: "warning.main" }}
										>
											<ReportProblemOutlined fontSize="small" />
										</Box>
									</Box>
								)}
								<Checkbox
									size="small"
									checked={params.data?.Applies ?? false}
									disabled={readOnly || lock !== undefined}
									onChange={(event) =>
										params.data &&
										onToggle(params.data.ProfileItemId, event.target.checked)
									}
									slotProps={{
										input: {
											...{ "data-test-id": "spec-dimension-applies-input" },
											"aria-label": getAppliesAriaLabel(params.data),
										},
									}}
								/>
							</Box>
						</Tooltip>
					</Box>
				);
			},
		},
		{
			field: "Code",
			headerName: "Dim",
			valueGetter: ({ data }) => data?.DisplayName ?? data?.Code,
			width: showType ? 120 : 72,
			sortable: false,
			resizable: true,
			tooltipField: "Name",
		},
		bandColumn(
			SPEC_BAND_FIELD.LTL,
			"Min",
			readOnly,
			onEdit,
			getError,
			angleInputs,
		),
		bandColumn(
			SPEC_BAND_FIELD.NOMINAL,
			"Target",
			readOnly,
			onEdit,
			getError,
			angleInputs,
		),
		bandColumn(
			SPEC_BAND_FIELD.UTL,
			"Max",
			readOnly,
			onEdit,
			getError,
			angleInputs,
		),
	];

	if (!showBands) columnDefs.splice(2, 3);

	if (showType) {
		columnDefs.splice(2, 0, {
			colId: "Type",
			headerName: "Type",
			width: 110,
			sortable: false,
			editable: false,
			cellRenderer: ({ data }: ICellRendererParams<SpecItemDraftRow>) => {
				if (!data) return null;
				if (isGaugeSpecItem(data)) return "Go/No Go";
				return data.ValueTypeCode === "A" ? "Checkbox" : "Decimal";
			},
		});
	}

	if (showDegrees && onDegreesChange) {
		columnDefs.splice(3, 0, {
			colId: "IsDegrees",
			headerName: "Use Degrees",
			headerComponentParams: {
				innerHeaderComponent: UseDegreesHeader,
			},
			width: 130,
			sortable: false,
			cellRenderer: ({ data }: ICellRendererParams<SpecItemDraftRow>) => {
				if (!data) return null;
				if (!isGaugeSpecItem(data) || readOnly)
					return isAngleSpecItem(data) ? "° / ′" : "—";
				return (
					<Checkbox
						size="small"
						checked={data.IsDegrees === true}
						disabled={!data.Applies}
						onChange={(event) =>
							onDegreesChange(data.ProfileItemId, event.target.checked)
						}
						slotProps={{
							input: { "aria-label": `${data.Name} (${data.Code}) in degrees` },
						}}
					/>
				);
			},
		});
	}

	return { columnDefs };
};
